import { useEffect, useState } from "react";
import { NavLink, Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
    BarChart3, Bell, FileText, Home, Images, LayoutDashboard, LogOut, Menu, MessageSquare, MessagesSquare, Package,
    BadgeCheck, Bot, ClipboardList, Download, FlaskConical, HelpCircle, Image as ImageIcon, Key, Megaphone, PenLine, Receipt, Repeat, RotateCcw, Scale, Search, Settings, ShieldCheck, ShoppingCart, Sparkles, Star, Store, Tags, Trash2, TrendingUp, Truck, User, UserCheck, Users, Volume2, Warehouse, Youtube,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useShop } from "../context/ShopContext";
import { api, API, ROLE_TR, fmtDate, speakText } from "../lib/api";
import { getPageUrl } from "../pages/legal/content";
import { logoIcon, useSiteContent } from "../lib/site";
import AiDrawer from "./AiDrawer";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Input } from "./ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { ScrollArea } from "./ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

const DEMO_EMAILS = ["erkankayatr@gmail.com", "alici@demo.ataangora.com", "tedarikci@demo.ataangora.com"];

const NAV = [
    { to: "/panel", label: "Panel", icon: LayoutDashboard, roles: ["buyer", "supplier", "admin"], end: true, tid: "nav-dashboard" },
    { to: "/panel/katalog", label: "Katalog", icon: Store, roles: ["buyer", "supplier", "admin"], tid: "nav-catalog" },
    { to: "/panel/tedarikciler", label: "Tedarikçiler", icon: Truck, roles: ["buyer"], tid: "nav-suppliers" },
    { to: "/panel/karsilastir", label: "Karşılaştır", icon: Scale, roles: ["buyer"], tid: "nav-compare" },
    { to: "/panel/sepet", label: "Sepet", icon: ShoppingCart, roles: ["buyer"], tid: "nav-cart" },
    { to: "/panel/kampanyalar", label: "Kampanyalar", icon: Sparkles, roles: ["buyer"], tid: "nav-campaigns" },
    { to: "/panel/teklifler", label: "Teklifler (RFQ)", icon: FileText, roles: ["buyer", "supplier"], tid: "nav-rfqs" },
    { to: "/panel/siparisler", label: "Siparişler", icon: Package, roles: ["buyer", "supplier", "admin"], tid: "nav-orders" },
    { to: "/panel/mesajlar", label: "Mesajlar", icon: MessageSquare, roles: ["buyer", "supplier", "admin"], tid: "nav-messages" },
    { to: "/panel/sohbet", label: "Sohbet Odası", icon: MessagesSquare, roles: ["buyer", "supplier", "admin"], tid: "nav-chatroom" },
    { to: "/panel/yonetim", label: "Yönetim Paneli", icon: Settings, roles: ["admin"], tid: "nav-admin" },
    { to: "/panel/cari-hesaplar", label: "Cari Hesaplar", icon: Receipt, roles: ["admin"], tid: "nav-cari" },
    { to: "/panel/faturalar", label: "Faturalar", icon: Receipt, roles: ["buyer", "supplier"], tid: "nav-invoices" },
    { to: "/panel/stok", label: "Stok & Envanter", icon: Warehouse, roles: ["supplier"], tid: "nav-inventory" },
    { to: "/panel/medya", label: "Ortam Kütüphanesi", icon: Images, roles: ["supplier", "admin"], tid: "nav-media" },
    { to: "/panel/firma", label: "Firma Bilgilerim", icon: Store, roles: ["supplier"], tid: "nav-firma" },
    { to: "/panel/satis-paneli", label: "Satış Paneli", icon: TrendingUp, roles: ["supplier"], tid: "nav-sales-hub" },
    { to: "/panel/raporlar", label: "Raporlar", icon: BarChart3, roles: ["buyer", "supplier"], tid: "nav-reports" },
    { to: "/panel/destek", label: "Destek", icon: MessageSquare, roles: ["buyer", "supplier"], tid: "nav-support" },
    { to: "/panel/api-erisimi", label: "API Erişimi", icon: Key, roles: ["buyer", "supplier"], tid: "nav-api-access" },
];

const ADMIN_MENU = [
    ["ANA MENÜ", [
        { label: "Katalog", to: "/panel/katalog", icon: Store, tid: "nav-catalog" },
        { label: "Mesajlar", to: "/panel/mesajlar", icon: MessageSquare, tid: "nav-messages" },
        { label: "Panel", to: "/panel", end: true, icon: LayoutDashboard, tid: "nav-dashboard" },
        { label: "Siparişler", to: "/panel/siparisler", icon: Package, tid: "nav-orders" },
        { label: "Sohbet Odası", to: "/panel/sohbet", icon: MessagesSquare, tid: "nav-chatroom" },
    ]],
    ["FİNANS & CARİ", [
        { label: "Cari Hesaplar", to: "/panel/cari-hesaplar", icon: Receipt, tid: "nav-cari" },
        { label: "Raporlar", tab: "reports", icon: TrendingUp, tid: "nav-admin-reports" },
    ]],
    ["ÜRÜN & KATALOG YÖNETİMİ", [
        { label: "Kategoriler", tab: "categories", icon: Tags, tid: "nav-admin-categories" },
        { label: "Ürünler", tab: "products", icon: Package, tid: "nav-admin-products" },
    ]],
    ["SATIŞ & SİPARİŞ YÖNETİMİ", [
        { label: "İade Talepleri", tab: "returns", icon: RotateCcw, tid: "nav-admin-returns" },
        { label: "Siparişler", tab: "orders", icon: ShoppingCart, tid: "nav-admin-orders" },
        { label: "Teklifler", tab: "rfqs", icon: FileText, tid: "nav-admin-rfqs" },
    ]],
    ["İLETİŞİM & DESTEK", [
        { label: "Destek", tab: "support", icon: UserCheck, tid: "nav-admin-support" },
        { label: "Sorular", tab: "questions", icon: HelpCircle, tid: "nav-admin-questions" },
        { label: "Şikayet & Ödül", tab: "complaints", icon: BadgeCheck, tid: "nav-admin-complaints" },
        { label: "Sohbet Odası", to: "/panel/sohbet", icon: MessagesSquare, tid: "nav-chatroom-destek" },
    ]],
    ["PAZARLAMA", [
        { label: "Kampanya Onayı", tab: "featured", icon: Star, tid: "nav-admin-featured" },
        { label: "Reklamlar", tab: "ads", icon: Megaphone, tid: "nav-admin-ads" },
    ]],
    ["İÇERİK & SİTE YÖNETİMİ", [
        { label: "Sayfalar", tab: "pages", icon: FileText, tid: "nav-admin-pages" },
        { label: "Site Logosu", tab: "logo", icon: ImageIcon, tid: "nav-admin-logo" },
        { label: "Site Metinleri", tab: "content", icon: PenLine, tid: "nav-admin-content" },
        { label: "SSS", tab: "faq", icon: HelpCircle, tid: "nav-admin-faq" },
        { label: "Videolar", tab: "videos", icon: Youtube, tid: "nav-admin-videos" },
        { label: "Profil Bilgilerim", tab: "profil", icon: User, tid: "nav-admin-profile" },
    ]],
    ["DOSYA & MEDYA", [
        { label: "Ortam Kütüphanesi", to: "/panel/medya", icon: Images, tid: "nav-media" },
    ]],
    ["BAŞVURU & ENTEGRASYON", [
        { label: "API Erişimi", tab: "apikeys", icon: Key, tid: "nav-admin-apikeys" },
        { label: "Başvurular", tab: "registrations", icon: ClipboardList, tid: "nav-admin-registrations" },
    ]],
    ["YÖNETİM & GÜVENLİK", [
        { label: "Kullanıcılar & Şifreler", tab: "users", icon: Users, tid: "nav-admin-users" },
        { label: "Admin Bilgileri", tab: "admininfo", icon: ShieldCheck, tid: "nav-admin-account" },
        { label: "Geri Dönüşüm Kutusu", tab: "recycle", icon: Trash2, tid: "nav-admin-recycle" },
        { label: "Test Verileri", tab: "testdata", icon: FlaskConical, tid: "nav-admin-testdata" },
        { label: "AI Yönetimi", tab: "ai", icon: Bot, tid: "nav-admin-ai" },
        { label: "Gizlilik Ayarları", tab: "privacy", icon: ShieldCheck, tid: "nav-admin-privacy" },
        { label: "Yönetim Paneli", to: "/panel/yonetim", icon: Settings, tid: "nav-admin" },
    ]],
];

const ROLE_BADGE = {
    buyer: "bg-emerald-600/20 text-emerald-300 border-emerald-600/40",
    supplier: "bg-saffron-400/20 text-saffron-400 border-saffron-400/40",
    admin: "bg-terra-500/20 text-terra-500 border-terra-500/40",
};

function SidebarNav({ onNavigate }) {
    const { user } = useAuth();
    const { cartCount } = useShop();
    const location = useLocation();
    const tabParam = new URLSearchParams(location.search).get("tab");

    if (user?.role === "admin") {
        return (
            <nav className="flex flex-col gap-3 p-4" data-testid="sidebar-nav">
                {ADMIN_MENU.map(([group, items]) => (
                    <div key={group}>
                        <p className="mb-1 px-3 text-[10px] font-bold uppercase tracking-wider text-stone-400">{group}</p>
                        <div className="flex flex-col gap-0.5">
                            {items.map((n) => {
                                const to = n.tab ? `/panel/yonetim?tab=${n.tab}` : n.to;
                                const active = n.tab
                                    ? location.pathname === "/panel/yonetim" && tabParam === n.tab
                                    : n.end
                                      ? location.pathname === n.to
                                      : n.to === "/panel/yonetim"
                                        ? location.pathname === "/panel/yonetim" && !tabParam
                                        : location.pathname.startsWith(n.to);
                                return (
                                    <NavLink
                                        key={n.tid}
                                        to={to}
                                        onClick={onNavigate}
                                        data-testid={n.tid}
                                        className={`flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors ${
                                            active ? "bg-primary text-primary-foreground shadow-sm" : "text-stone-600 hover:bg-muted hover:text-foreground"
                                        }`}
                                    >
                                        <n.icon className="h-4 w-4 shrink-0" />
                                        <span className="flex-1">{n.label}</span>
                                    </NavLink>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </nav>
        );
    }

    return (
        <nav className="flex flex-col gap-1 p-4" data-testid="sidebar-nav">
            {NAV.filter((n) => n.roles.includes(user?.role)).map((n) => (
                <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    onClick={onNavigate}
                    data-testid={n.tid}
                    className={({ isActive }) =>
                        `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                            isActive
                                ? "bg-primary text-primary-foreground shadow-sm"
                                : "text-stone-600 hover:bg-muted hover:text-foreground"
                        }`
                    }
                >
                    <n.icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1">{n.label}</span>
                    {n.tid === "nav-cart" && cartCount > 0 && (
                        <span className="rounded-full bg-terra-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{cartCount}</span>
                    )}
                </NavLink>
            ))}
        </nav>
    );
}

function NotificationsPopover() {
    const { unread, refreshNotifications } = useShop();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);

    const load = async () => {
        const r = await api.get("/notifications");
        setItems(r.data.items);
    };

    const readAll = async () => {
        await api.post("/notifications/read-all");
        await refreshNotifications();
        load();
    };

    return (
        <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
            <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative text-stone-300 hover:text-white hover:bg-stone-800" data-testid="notifications-button">
                    <Bell className="h-5 w-5" />
                    {unread > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-terra-500 px-1 text-[10px] font-bold text-white" data-testid="notifications-badge">
                            {unread}
                        </span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="end" data-testid="notifications-panel">
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <p className="font-display font-bold text-sm">Bildirimler</p>
                    <button onClick={readAll} className="text-xs text-primary hover:underline" data-testid="notifications-read-all">Tümünü okundu say</button>
                </div>
                <ScrollArea className="max-h-80">
                    {items.length === 0 && <p className="p-4 text-sm text-muted-foreground">Bildirim yok.</p>}
                    {items.map((n) => (
                        <div key={n.notification_id} className={`border-b px-4 py-3 last:border-0 ${n.read ? "opacity-60" : ""}`}>
                            <div className="flex items-start gap-2">
                                {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-terra-500 animate-pulse-dot" />}
                                <div className={`flex-1 ${n.read ? "pl-4" : ""}`}>
                                    <p className="text-sm font-semibold leading-tight">{n.title}</p>
                                    <p className="mt-0.5 text-xs text-muted-foreground">{n.body}</p>
                                    <p className="mt-1 text-[10px] uppercase tracking-wide text-stone-400">{fmtDate(n.created_at)}</p>
                                </div>
                                <button
                                    onClick={() => speakText(`${n.title}. ${n.body}`).catch((e) => toast.error(e.message))}
                                    className="shrink-0 text-stone-400 transition-colors hover:text-terra-600"
                                    title="Sesli oku"
                                    data-testid={`notification-listen-${n.notification_id}`}
                                >
                                    <Volume2 className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </ScrollArea>
            </PopoverContent>
        </Popover>
    );
}

export default function Layout() {
    const { user, logout, switchRole } = useAuth();
    const { cartCount, setAiOpen, compareIds } = useShop();
    const siteContent = useSiteContent();
    const navigate = useNavigate();
    const [q, setQ] = useState("");
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const [legalPages, setLegalPages] = useState([]);
    const canSwitch = DEMO_EMAILS.includes(user?.email);

    useEffect(() => {
        api.get("/pages").then((r) => setLegalPages(r.data)).catch(() => {});
    }, []);

    const doSearch = (e) => {
        e.preventDefault();
        setMobileSearchOpen(false);
        navigate(`/panel/katalog${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    };

    const doSwitch = async (role) => {
        try {
            await switchRole(role);
            toast.success(`Görünüm değiştirildi: ${ROLE_TR[role]}`);
            navigate("/panel");
        } catch {
            toast.error("Rol değiştirilemedi");
        }
    };

    return (
        <div className="min-h-screen bg-background" data-testid="app-layout">
            <header className="sticky top-0 z-40 border-b border-stone-800 bg-stone-950/95 text-stone-100 backdrop-blur-md">
                <div className="flex h-16 items-center gap-3 px-4 sm:px-6">
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <Button variant="ghost" size="icon" className="md:hidden text-stone-300" data-testid="mobile-menu-button">
                                <Menu className="h-5 w-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="w-72 overflow-y-auto p-0">
                            <div className="flex items-center gap-2 border-b p-4">
                                <img src={logoIcon(siteContent)} alt="ATA Angora" className="h-8 w-8 object-contain" />
                                <span className="font-display text-lg font-bold"><span className="text-terra-600">ata</span> <span className="text-sage-600">angora</span></span>
                            </div>
                            <SidebarNav onNavigate={() => setMobileOpen(false)} />
                        </SheetContent>
                    </Sheet>

                    <NavLink to="/panel" className="flex items-center gap-2.5" data-testid="header-logo">
                        <img src={logoIcon(siteContent)} alt="ATA Angora" className="h-9 w-9 object-contain" />
                        <div className="leading-none hidden sm:block">
                            <span className="font-display text-lg font-bold tracking-tight">
                                <span className="text-terra-500">ata</span> <span className="text-emerald-400">angora</span>
                            </span>
                            <p className="text-[10px] uppercase tracking-widest text-stone-400">Tedarik Platformu</p>
                        </div>
                    </NavLink>

                    <form onSubmit={doSearch} className="ml-4 hidden max-w-md flex-1 items-center gap-2 sm:flex">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-500" />
                            <Input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Ürün ara: pul biber, antep fıstığı, mercimek..."
                                className="border-stone-700 bg-stone-900 pl-9 text-sm text-stone-100 placeholder:text-stone-500 focus-visible:ring-terra-500"
                                data-testid="global-search-input"
                            />
                        </div>
                        <Button type="submit" size="sm" className="bg-terra-600 text-white hover:bg-terra-700" data-testid="global-search-button">
                            <Search className="h-4 w-4" /><span className="ml-1.5 hidden lg:inline">Ara</span>
                        </Button>
                    </form>

                    <div className="ml-auto flex items-center gap-1.5">
                        <Button
                            onClick={() => setAiOpen(true)}
                            className="hidden sm:flex bg-gradient-to-r from-terra-600 to-terra-500 text-white hover:from-terra-700 hover:to-terra-600"
                            data-testid="ai-assistant-open-button"
                        >
                            <Sparkles className="mr-2 h-4 w-4" /> AI Asistan
                        </Button>
                        <Button variant="ghost" size="icon" className="sm:hidden text-stone-300" onClick={() => setMobileSearchOpen((v) => !v)} data-testid="mobile-search-toggle">
                            <Search className="h-5 w-5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="sm:hidden text-stone-300" onClick={() => setAiOpen(true)} data-testid="ai-assistant-open-button-mobile">
                            <Sparkles className="h-5 w-5" />
                        </Button>
                        {compareIds.length > 0 && (
                            <Button variant="ghost" size="icon" className="relative text-stone-300 hover:text-white hover:bg-stone-800" onClick={() => navigate("/panel/karsilastir")} data-testid="compare-button">
                                <Scale className="h-5 w-5" />
                                <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-saffron-400 px-1 text-[10px] font-bold text-stone-900">{compareIds.length}</span>
                            </Button>
                        )}
                        {user?.role === "buyer" && (
                            <Button variant="ghost" size="icon" className="relative text-stone-300 hover:text-white hover:bg-stone-800" onClick={() => navigate("/panel/sepet")} data-testid="cart-button">
                                <ShoppingCart className="h-5 w-5" />
                                {cartCount > 0 && (
                                    <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-terra-500 px-1 text-[10px] font-bold text-white" data-testid="cart-count-badge">{cartCount}</span>
                                )}
                            </Button>
                        )}
                        <NotificationsPopover />

                        {canSwitch && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button className={`ml-1 hidden sm:inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${ROLE_BADGE[user?.role]}`} data-testid="role-switcher">
                                        <Repeat className="h-3 w-3" /> {ROLE_TR[user?.role]}
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuLabel>Görünüm Değiştir (Demo)</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => doSwitch("buyer")} data-testid="switch-role-buyer">Alıcı — Lezzet Restoran</DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => doSwitch("supplier")} data-testid="switch-role-supplier">Tedarikçi — Anadolu Baharat</DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => doSwitch("admin")} data-testid="switch-role-admin">Admin — Erkan Kaya</DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                        {!canSwitch && <Badge className={`ml-1 hidden sm:inline-flex border ${ROLE_BADGE[user?.role]}`} data-testid="role-badge">{ROLE_TR[user?.role]}</Badge>}

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button data-testid="user-menu-button">
                                    <Avatar className="h-8 w-8 border border-stone-700">
                                        <AvatarImage src={user?.picture || undefined} />
                                        <AvatarFallback className="bg-terra-600 text-xs font-bold text-white">
                                            {(user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                                        </AvatarFallback>
                                    </Avatar>
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-64">
                                <DropdownMenuLabel>
                                    <p className="font-semibold">{user?.name}</p>
                                    <p className="text-xs font-normal text-muted-foreground">{user?.email}</p>
                                    {user?.company && <p className="mt-1 text-xs font-normal text-primary">{user.company.name}</p>}
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem asChild>
                                    <Link to="/" data-testid="back-to-site"><Home className="mr-2 h-4 w-4" /> Siteye Dön</Link>
                                </DropdownMenuItem>
                                {(user?.role === "supplier" || user?.role === "buyer") && (
                                    <DropdownMenuItem asChild>
                                        <a href={`${API}/backup`} data-testid="backup-download-link"><Download className="mr-2 h-4 w-4" /> Verilerimi Yedekle</a>
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="logout-button">
                                    <LogOut className="mr-2 h-4 w-4" /> Çıkış Yap
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
                {mobileSearchOpen && (
                    <form onSubmit={doSearch} className="flex items-center gap-2 border-t border-stone-800 px-4 py-2.5 sm:hidden">
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Ürün ara..."
                            autoFocus
                            className="border-stone-700 bg-stone-900 text-sm text-stone-100 placeholder:text-stone-500"
                            data-testid="mobile-search-input"
                        />
                        <Button type="submit" size="sm" className="bg-terra-600 text-white hover:bg-terra-700" data-testid="mobile-search-button">Ara</Button>
                    </form>
                )}
            </header>

            <div className="flex">
                <aside className="sticky top-16 hidden h-[calc(100vh-4rem)] w-60 shrink-0 overflow-y-auto border-r bg-card scrollbar-thin md:block">
                    <SidebarNav />
                </aside>
                <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
                    <Outlet />
                    <footer className="mt-14 border-t pt-5 pb-2" data-testid="panel-footer">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                            {legalPages.map((p) => (
                                <Link key={p.page_id} to={getPageUrl(p)} className="text-[11px] text-muted-foreground transition-colors hover:text-primary">
                                    {p.title}
                                </Link>
                            ))}
                            <Link to="/iletisim" className="text-[11px] text-muted-foreground transition-colors hover:text-primary">İletişim</Link>
                            <span className="ml-auto text-[10px] text-stone-400">© 2026 ATA Angora</span>
                        </div>
                    </footer>
                </main>
            </div>

            <AiDrawer />
        </div>
    );
}
